import * as React from 'react'
import { cn } from '@/lib/utils'

interface TabsContextValue {
  value: string
  onValueChange: (value: string) => void
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

export interface TabsProps {
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  children?: React.ReactNode
  className?: string
}

export function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue || '')
  const currentValue = value !== undefined ? value : internalValue
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue)
      }
      onValueChange?.(newValue)
    },
    [value, onValueChange]
  )

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div className={cn('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  )
}

export interface TabsListProps {
  children?: React.ReactNode
  className?: string
}

/**
 * Tabs on one line: where they do not fit (a phone, a narrow window beside the
 * sidebar) the row scrolls sideways instead of wrapping a tab onto a second
 * line under the others.
 */
const TAB_TRIGGER_CLASSES =
  'relative shrink-0 whitespace-nowrap rounded-sm pb-3 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mint disabled:pointer-events-none disabled:opacity-50'

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div className={cn('relative border-b border-border', className)}>
      <div className="flex items-center gap-x-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {children}
      </div>
    </div>
  )
}

export interface TabsTriggerProps {
  value: string
  children?: React.ReactNode
  className?: string
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs')
  }

  const isActive = context.value === value
  const ref = React.useRef<HTMLButtonElement>(null)
  // In a row that scrolls, the selected tab is brought into view (a page opened
  // on its last tab on a phone would otherwise show none selected). Only the
  // row scrolls, never the page.
  React.useEffect(() => {
    const tab = ref.current
    const row = tab?.parentElement
    if (!isActive || !tab || !row || row.scrollWidth <= row.clientWidth) return
    const start = tab.offsetLeft - row.offsetLeft
    const end = start + tab.offsetWidth
    if (start < row.scrollLeft) row.scrollLeft = start
    else if (end > row.scrollLeft + row.clientWidth) row.scrollLeft = end - row.clientWidth
  }, [isActive])

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => context.onValueChange(value)}
      className={cn(
        TAB_TRIGGER_CLASSES,
        isActive ? 'text-text' : 'text-textMuted hover:text-text',
        className
      )}
    >
      {children}
      {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-mint" />}
    </button>
  )
}

export interface TabsContentProps {
  value: string
  children?: React.ReactNode
  className?: string
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('TabsContent must be used within Tabs')
  }

  if (context.value !== value) {
    return null
  }

  return <div className={cn('mt-6', className)}>{children}</div>
}
